import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, ArrowLeft, Gift, MapPin, SealCheck, Storefront } from "@phosphor-icons/react";
import { Html5Qrcode } from "html5-qrcode";
import api, { apiError } from "@/lib/api";
import { pts, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function ScanBusiness() {
  const [manual, setManual] = useState("");
  const [scanning, setScanning] = useState(false);
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const scannerRef = useRef(null);

  const resolve = async (token) => {
    if (!token) return toast.error("Scan or paste a business QR");
    setBusy(true);
    try {
      const { data } = await api.post("/customer/scan", { token: token.trim() });
      setProfile(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startCamera = () => {
    setScanning(true);
    setTimeout(async () => {
      try {
        const scanner = new Html5Qrcode("biz-qr-reader");
        scannerRef.current = scanner;
        await scanner.start({ facingMode: "environment" }, { fps: 10, qrbox: 220 },
          async (decoded) => { await stopCamera(); resolve(decoded); }, () => {});
      } catch (e) {
        toast.error("Camera unavailable — paste the code instead");
        setScanning(false);
      }
    }, 100);
  };

  const stopCamera = async () => {
    try { await scannerRef.current?.stop(); scannerRef.current?.clear(); } catch { /* already stopped */ }
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { stopCamera(); }, []);

  if (profile) {
    return (
      <div className="pt-2 space-y-5">
        <button onClick={() => setProfile(null)} data-testid="scan-biz-back" className="text-muted-foreground flex items-center gap-1 text-sm"><ArrowLeft size={16} /> Scan another</button>

        <div className="card-surface p-6" data-testid="scanned-business">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 rounded-2xl bg-primary/15 flex items-center justify-center overflow-hidden">
              {profile.logo ? <img src={profile.logo} alt="" className="h-full w-full object-cover" /> : <span className="font-display font-bold text-primary text-xl">{initials(profile.name)}</span>}
            </div>
            <div className="min-w-0">
              <h1 className="font-display font-bold text-xl truncate" data-testid="scanned-business-name">{profile.name}</h1>
              {profile.onekite_name && <p className="text-primary text-sm">@{profile.onekite_name}</p>}
              {profile.verified && <p className="text-xs text-primary flex items-center gap-1 mt-0.5" data-testid="scanned-business-verified"><SealCheck size={14} weight="fill" /> Verified on OneKite</p>}
            </div>
          </div>
          {profile.branch && (
            <p className="mt-4 text-sm text-muted-foreground flex items-center gap-1.5" data-testid="scanned-branch"><MapPin size={15} className="text-primary" /> {profile.branch.name}{profile.branch.address ? ` · ${profile.branch.address}` : ""}</p>
          )}
          <div className="mt-5 border-t border-black/10 pt-4">
            <p className="text-xs text-muted-foreground">Your points here</p>
            <p className="font-display font-extrabold text-3xl text-primary" data-testid="scanned-points">{pts(profile.points)}<span className="text-base text-muted-foreground ml-1">pts</span></p>
            {!profile.is_member && <p className="text-xs text-muted-foreground mt-1">You&apos;re not a member yet — the cashier scans your QR to start earning.</p>}
          </div>
        </div>

        <div>
          <h2 className="font-display font-semibold mb-3 flex items-center gap-2"><Gift size={18} className="text-primary" /> Available rewards</h2>
          <div className="space-y-2">
            {profile.rewards.length === 0 && <p className="text-sm text-muted-foreground">No rewards yet.</p>}
            {profile.rewards.map((r) => (
              <div key={r.id} data-testid="scanned-reward" className={`card-surface p-4 flex items-center justify-between ${r.eligible ? "border-primary/40" : ""}`}>
                <div><p className="font-medium">{r.name}</p><p className="text-xs text-muted-foreground">{r.description}</p></div>
                <div className="text-right"><p className="font-display font-bold text-primary">{pts(r.points_required)}</p><p className={`text-[10px] ${r.eligible ? "text-primary" : "text-muted-foreground"}`}>{r.eligible ? "Eligible" : "Keep earning"}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-2 space-y-5">
      <div>
        <h1 className="font-display font-bold text-2xl flex items-center gap-2"><Storefront size={24} className="text-primary" /> Scan a business</h1>
        <p className="text-sm text-muted-foreground">Scan a OneKite business or branch QR to open its profile, your points, and rewards.</p>
      </div>

      {scanning ? (
        <div className="card-surface p-4">
          <div id="biz-qr-reader" className="rounded-xl overflow-hidden" />
          <Button data-testid="scan-biz-stop-camera" variant="outline" onClick={stopCamera} className="w-full mt-3 rounded-xl border-black/10">Stop camera</Button>
        </div>
      ) : (
        <Button data-testid="scan-biz-camera" onClick={startCamera} className="w-full h-14 rounded-2xl bg-primary/15 text-primary hover:bg-primary/20 font-semibold border border-primary/20">
          <Camera size={22} className="mr-2" /> Open camera scanner
        </Button>
      )}

      <div className="card-surface p-5 space-y-3">
        <p className="text-xs text-muted-foreground uppercase tracking-wider font-mono">Or paste code</p>
        <Input data-testid="scan-biz-token" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="OK1.…" className="bg-muted/40 border-black/10 h-11 rounded-xl font-mono text-xs" />
        <Button data-testid="scan-biz-resolve" onClick={() => resolve(manual)} disabled={busy} className="w-full h-11 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 font-semibold">
          {busy ? "Opening…" : "Open business"}
        </Button>
      </div>
    </div>
  );
}
